import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {FaIcon} from '../FaIcon';
import {usePortalClient} from '../../context/PortalClientContext';

const SEARCH_THRESHOLD = 6;

/**
 * Switcher del cliente activo del portal (multi-cliente). Dropdown en la topbar, entre el
 * logo y el nav. Con un solo cliente (o en preview) devuelve null: la topbar queda idéntica
 * a la de siempre. Al elegir, el contenido remonta vía key={activeClientId} en LayoutPortal
 * (sin hard reload: el token y la sesión no cambian) y el guard de módulos redirige si la
 * sección actual no existe para el cliente nuevo.
 */
export const PortalClientSwitcher = () => {
    const {t} = useTranslation();
    const {isPreview, clients, activeClient, isMultiClient, setActiveClient} = usePortalClient();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const wrapperRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    if (isPreview || !isMultiClient) return null;

    const normalizedSearch = search.trim().toLowerCase();
    const filtered = normalizedSearch
        ? clients.filter(client => (client.name || '').toLowerCase().includes(normalizedSearch))
        : clients;

    const handleSelect = (client) => {
        setOpen(false);
        setSearch('');
        if (!activeClient || client.id !== activeClient.id) {
            setActiveClient(client.id);
        }
    };

    return (
        <div className="portal-client-switcher" ref={wrapperRef}>
            <button
                className="portal-client-switcher__trigger"
                onClick={() => setOpen(!open)}
                aria-haspopup="listbox"
                aria-expanded={open}
                title={t('portal.switcher.change')}
            >
                <span className="portal-client-switcher__name">
                    {activeClient ? activeClient.name : t('portal.switcher.change')}
                </span>
                <FaIcon iconName={open ? 'chevron-up' : 'chevron-down'} />
            </button>

            {open && (
                <div className="portal-client-switcher__menu" role="listbox">
                    {clients.length > SEARCH_THRESHOLD && (
                        <input
                            type="search"
                            className="portal-client-switcher__search"
                            placeholder={t('portal.switcher.search')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            autoFocus
                        />
                    )}
                    {filtered.map(client => (
                        <button
                            key={client.id}
                            role="option"
                            aria-selected={activeClient ? client.id === activeClient.id : false}
                            className={`portal-client-switcher__item ${activeClient && client.id === activeClient.id ? '-active' : ''}`}
                            onClick={() => handleSelect(client)}
                        >
                            {activeClient && client.id === activeClient.id
                                ? <FaIcon iconName="check" />
                                : <span className="portal-client-switcher__spacer" aria-hidden="true"/>}
                            <span className="portal-client-switcher__item-name">{client.name}</span>
                        </button>
                    ))}
                    {filtered.length === 0 && (
                        <div className="portal-client-switcher__empty">{t('portal.switcher.no_results')}</div>
                    )}
                </div>
            )}
        </div>
    );
};
